import React from 'react';
import { TranslationLanguage, languageInfo } from '@lingoping/core';
import { ARABIC_SCRIPT_FONT_STACK, ensureTranslationFont } from '../utils/translationFont.js';

ensureTranslationFont();

/**
 * Typography for text in a translation language. Arabic-script languages get the bundled
 * Vazirmatn font and a taller line height (the script has marks above and below the line);
 * `unicode-bidi: isolate` keeps a German name inside an RTL sentence, and the sentence's own
 * punctuation, in the right place without leaking into the surrounding layout.
 */
export function translationTextStyle(lang: TranslationLanguage): React.CSSProperties {
  const info = languageInfo(lang);
  const style: React.CSSProperties = { unicodeBidi: 'isolate', lineHeight: 1.5 };
  if (info.script === 'arabic') {
    style.fontFamily = ARABIC_SCRIPT_FONT_STACK;
    style.lineHeight = 1.8;
  }
  return style;
}

interface Props extends Omit<React.HTMLAttributes<HTMLElement>, 'lang' | 'dir' | 'children'> {
  text: string;
  /** The language the text is actually in (what `translationFor` returns, not what was asked for). */
  lang: TranslationLanguage;
  /** The element to render; a `div` by default. */
  as?: 'div' | 'span' | 'p' | 'h4';
}

/** The one place a translated meaning or example sentence is rendered. */
export const TranslationText: React.FC<Props> = ({
  text,
  lang,
  as: Tag = 'div',
  style,
  ...rest
}) => (
  <Tag
    {...rest}
    lang={lang}
    dir={languageInfo(lang).dir}
    style={{ ...translationTextStyle(lang), ...style }}
  >
    {text}
  </Tag>
);

/** A language's own name ("فارسی") in its script and direction, for pickers and menus. */
export const NativeLanguageName: React.FC<{ lang: TranslationLanguage }> = ({ lang }) => {
  const info = languageInfo(lang);
  return (
    <span lang={info.code} dir={info.dir} style={translationTextStyle(info.code)}>
      {info.nativeName}
    </span>
  );
};
